/**
 * Decision Making Assist — the screen over the engine.
 *
 * The engine (`domain/decision.ts`) computes every number; this screen shows
 * each of them with its working and its sources, lets a person enter what
 * the platform lacks, runs the solver on the quarter's constraints, writes
 * the executive case, asks a connected model to explain — checked against
 * the engine's figures — and logs the decision. See docs/DECISION-ASSIST.md.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { askModel, hasModel, loadConfig } from "../../assistant/external";
import type { ClosureCost } from "../../domain/capacity";
import type { Closure } from "../../domain/closures";
import { briefWindowFor } from "../../domain/dbrief";
import {
  CATEGORIES,
  ZONE_CLASSES,
  answerFollowUp,
  defaultConstraints,
  defaultRateCard,
  materialForModel,
  MODEL_SYSTEM_PROMPT,
  recordFor,
  runEngine,
  verifyFigures,
  type Constraints,
  type DecisionItem,
  type DecisionRecord,
  type EngineInputs,
  type EngineOutput,
  type EnteredInputs,
  type Option,
  type RateCard,
  type SourceRef,
} from "../../domain/decision";
import { money } from "../../domain/intelligence";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import { useIntelligence, type Gathered } from "../intelligence/gather";
import * as store from "./store";
import s from "./Decision.module.css";
import { t, tn } from "../../i18n";

/** The engine's weight keys, as a reader sees them. */
const WEIGHT_LABEL: Record<string, string> = {
  consequence: "consequence",
  timeToThreshold: "time to threshold",
  regulatoryExposure: "regulatory exposure",
  deferralCost: "deferral cost",
  windowConstraint: "window constraint",
};

const TEST_FOR: Record<string, string[]> = {
  pavement: ["friction", "pci", "pcr"],
  marking: ["retroreflectivity"],
  agl: ["photometric", "agl_insulation"],
};

export interface Records {
  closures: Closure[];
  closureCosts: Record<string, ClosureCost | null>;
  liability: EngineInputs["liability"];
  testTypes: EngineInputs["testTypes"];
  histories: EngineInputs["histories"];
  resolvedPredictions: number;
  rateCard: RateCard | null;
  constraints: Constraints | null;
  entered: EnteredInputs;
  log: DecisionRecord[];
}

export function useRecords(g: Gathered | undefined) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["decision-records", siteId, g?.report.generatedAt ?? ""],
    enabled: !!g,
    staleTime: 60_000,
    queryFn: async (): Promise<Records> => {
      const now = new Date();
      const since = new Date(now.getTime() - 180 * 86_400_000).toISOString();
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const [closures, liability, testing, drift, rateCard, constraints, entered, log] = await Promise.all([
        quiet(source.listClosures(siteId, since), [] as Closure[]),
        quiet(source.liabilityRegister(siteId), null),
        quiet(source.getTestingSummary(siteId), null),
        quiet(source.driftChecks(siteId), []),
        store.loadRateCard(source, siteId),
        store.loadConstraints(source, siteId),
        store.loadEntered(source, siteId),
        store.loadLog(source, siteId),
      ]);
      const findings = g!.report.findings;
      const zones = [
        ...new Set(
          findings
            .map((f) => f.where?.match(/\b(RWY-[\w-]+|TWY-[\w-]+|STAND-\w+|[A-Z]\d{1,2})\b/)?.[1])
            .filter((z): z is string => !!z),
        ),
      ];
      const closureCosts: Record<string, ClosureCost | null> = {};
      await Promise.all(
        zones.map(async (zone) => {
          const w = briefWindowFor(zone, closures, now);
          closureCosts[zone] = await quiet(
            source.closureCost({ siteId, positionCode: zone, from: w.from, to: w.to }),
            null,
          );
        }),
      );
      /* per-asset test history, for the trend, where the finding names an asset */
      const histories: EngineInputs["histories"] = {};
      const assets = [
        ...new Set(findings.map((f) => f.where).filter((w): w is string => !!w && /^[A-Z]{2,}-[\w-]+$/.test(w))),
      ];
      await Promise.all(
        assets.flatMap((asset) =>
          Object.values(TEST_FOR)
            .flat()
            .map(async (testType) => {
              const pts = await quiet(source.getAssetTestHistory(siteId, testType, asset), []);
              if (pts.length)
                histories[`${testType}:${asset}`] = pts.map((p) => ({
                  performedOn: p.performedOn,
                  value: p.value,
                  specified: p.specified,
                  reference: p.reference,
                  recordId: p.recordId,
                }));
            }),
        ),
      );
      return {
        closures,
        closureCosts,
        liability,
        testTypes: testing?.types ?? [],
        histories,
        resolvedPredictions: drift.reduce((t, d) => t + (d.decided ?? 0), 0),
        rateCard,
        constraints,
        entered,
        log,
      };
    },
  });
}

export function DecisionAssistPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const records = useRecords(gathered.data);
  const pending = gathered.isPending || (gathered.data && records.isPending);
  return (
    <Page eyebrow={`${session.site.code} · ${t("Executive")} · ${t("Decision Room")}`} title="The engine">
      <p className={s.lead}>
        {t(
          "The arithmetic is separated from the argument. Every number here is computed from a platform record or a value a named person entered, with its working beside it. A connected model only explains those numbers and is checked against them; it never produces one.",
        )}
      </p>
      {pending && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {records.isError && <ErrorState error={records.error} retry={() => void records.refetch()} />}
      {gathered.data && records.data && <Module g={gathered.data} r={records.data} />}
    </Page>
  );
}

function Module({ g, r }: { g: Gathered; r: Records }) {
  const session = useSession();
  const qc = useQueryClient();
  const source = session.source;
  const siteId = session.site.id;
  const who = session.principal.name;
  const [rateCard, setRateCard] = useState<RateCard>(r.rateCard ?? defaultRateCard());
  const [constraints, setConstraints] = useState<Constraints>(r.constraints ?? defaultConstraints(new Date()));
  const [entered, setEntered] = useState<EnteredInputs>(r.entered);
  const [log, setLog] = useState<DecisionRecord[]>(r.log);
  const [savedTo, setSavedTo] = useState<string | null>(null);

  const out = useMemo<EngineOutput>(
    () =>
      runEngine({
        now: new Date(),
        report: g.report,
        workOrders: g.inputs.workOrders,
        closures: r.closures,
        closureCosts: r.closureCosts,
        liability: r.liability,
        testTypes: r.testTypes,
        histories: r.histories,
        rateCard,
        entered,
        constraints,
        resolvedPredictions: r.resolvedPredictions,
      }),
    [g, r, rateCard, entered, constraints],
  );

  const persist = async (what: "card" | "constraints" | "entered", value: RateCard | Constraints | EnteredInputs) => {
    const where =
      what === "card"
        ? await store.saveRateCard(source, siteId, value as RateCard)
        : what === "constraints"
          ? await store.saveConstraints(source, siteId, value as Constraints)
          : await store.saveEntered(source, siteId, value as EnteredInputs);
    setSavedTo(
      where === "platform"
        ? t("saved on the platform, under your name")
        : t("saved in this browser only — the platform did not take the note"),
    );
    void qc.invalidateQueries({ queryKey: ["decision-records"] });
  };

  const decide = async (item: DecisionItem, option: Option["key"], rationale: string, mitigation?: string) => {
    const rec = recordFor(item, option, who, rationale, out.generatedAt, mitigation);
    const where = await store.appendLog(source, siteId, rec);
    setLog((l) => [rec, ...l]);
    setSavedTo(
      where === "platform"
        ? t("decision logged on the platform under {name}", { name: who })
        : t("decision kept in this browser only — the platform did not take the note"),
    );
  };

  return (
    <div className={s.module}>
      <div className={s.status}>
        <span className={s.gate}>{out.confidence.note}</span>
        {savedTo && <span className={s.saved}>{savedTo}</span>}
      </div>
      <ConstraintsEditor
        c={constraints}
        onChange={(c) => setConstraints(c)}
        onSave={() => void persist("constraints", constraints)}
      />
      <Items
        out={out}
        entered={entered}
        onEnter={(id, patch) => {
          const next = {
            ...entered,
            [id]: { ...entered[id], ...patch, enteredBy: who, enteredAt: new Date().toISOString() },
          };
          setEntered(next);
          void persist("entered", next);
        }}
        onDecide={decide}
      />
      <PortfolioView out={out} />
      <CaseView key={out.generatedAt} out={out} onResolve={(patch) => setConstraints({ ...constraints, ...patch })} />
      <RateCardEditor
        card={rateCard}
        onChange={setRateCard}
        onSave={() => void persist("card", { ...rateCard, enteredBy: who, enteredAt: new Date().toISOString() })}
      />
      <LogView log={log} />
    </div>
  );
}

/* ── constraints ──────────────────────────────────────────────────────── */

export function ConstraintsEditor({
  c,
  onChange,
  onSave,
}: {
  c: Constraints;
  onChange: (c: Constraints) => void;
  onSave: () => void;
}) {
  const num = (k: "windows" | "budget" | "crews" | "nightsPerCrew") => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...c, [k]: Number(e.target.value) || 0 });
  return (
    <Card
      title={t("The quarter — {quarter}", { quarter: c.quarter })}
      sub="the resources the solver allocates, and the weights the score uses; both visible, both editable"
    >
      <div className={s.grid4}>
        <Field label="Night windows">
          <Input type="number" min={0} value={c.windows} onChange={num("windows")} aria-label={t("Night windows")} />
        </Field>
        <Field label="Budget">
          <Input type="number" min={0} step={1000} value={c.budget} onChange={num("budget")} aria-label={t("Budget")} />
        </Field>
        <Field label="Crews">
          <Input type="number" min={0} value={c.crews} onChange={num("crews")} aria-label={t("Crews")} />
        </Field>
        <Field label="Nights per crew">
          <Input
            type="number"
            min={0}
            value={c.nightsPerCrew}
            onChange={num("nightsPerCrew")}
            aria-label={t("Nights per crew")}
          />
        </Field>
        <Field label="Scarce crew (optional)">
          <Input
            value={c.scarceCrew ?? ""}
            onChange={(e) => onChange({ ...c, scarceCrew: e.target.value || undefined })}
            placeholder={t("e.g. Pavements")}
          />
        </Field>
        <Field label="Season (optional)">
          <Input
            value={c.season ?? ""}
            onChange={(e) => onChange({ ...c, season: e.target.value || undefined })}
            placeholder={t("e.g. winter")}
          />
        </Field>
      </div>
      <div className={s.weights}>
        {(Object.keys(c.weights) as (keyof Constraints["weights"])[]).map((k) => (
          <label key={k} className={s.weight}>
            <span>{t(WEIGHT_LABEL[k] ?? k.replace(/([A-Z])/g, " $1").toLowerCase())}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={c.weights[k]}
              onChange={(e) => onChange({ ...c, weights: { ...c.weights, [k]: Number(e.target.value) } })}
              aria-label={t("weight {name}", { name: k })}
            />
            <b>{c.weights[k].toFixed(2)}</b>
          </label>
        ))}
      </div>
      <div className={s.row}>
        <Button onClick={onSave}>{t("Save the quarter")}</Button>
      </div>
    </Card>
  );
}

/* ── items ────────────────────────────────────────────────────────────── */

function Items({
  out,
  entered,
  onEnter,
  onDecide,
}: {
  out: EngineOutput;
  entered: EnteredInputs;
  onEnter: (id: string, patch: EnteredInputs[string]) => void;
  onDecide: (item: DecisionItem, option: Option["key"], rationale: string, mitigation?: string) => Promise<void>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const mandatory = out.items.filter((i) => i.mandatory.is);
  const rest = out.items.filter((i) => !i.mandatory.is);
  return (
    <>
      <Card
        title="Not a decision"
        sub={t("{n} mandatory — below a limit, so removed from the trade-off and done first", { n: mandatory.length })}
      >
        {mandatory.length === 0 ? (
          <p className={s.note}>{t("Nothing on the platform is past a limit.")}</p>
        ) : (
          <div className={s.items}>
            {mandatory.map((i) => (
              <ItemRow
                key={i.id}
                item={i}
                out={out}
                open={open === i.id}
                onToggle={() => setOpen(open === i.id ? null : i.id)}
                entered={entered[i.id]}
                onEnter={onEnter}
                onDecide={onDecide}
              />
            ))}
          </div>
        )}
      </Card>
      <Card
        title="Discretionary items, scored"
        sub="five components, each with its working; the total rests only on the components that exist"
      >
        {rest.length === 0 ? (
          <p className={s.note}>{t("Nothing discretionary on the platform.")}</p>
        ) : (
          <div className={s.items}>
            {rest.map((i) => (
              <ItemRow
                key={i.id}
                item={i}
                out={out}
                open={open === i.id}
                onToggle={() => setOpen(open === i.id ? null : i.id)}
                entered={entered[i.id]}
                onEnter={onEnter}
                onDecide={onDecide}
              />
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

function Refs({ list }: { list: SourceRef[] }) {
  if (!list.length) return <span className={s.noRef}>{t("no source")}</span>;
  return (
    <span className={s.refs}>
      {list.slice(0, 4).map((e) => (
        <span
          key={`${e.kind}:${e.ref}`}
          className={`${s.ref} ${e.kind === "entered" || e.kind === "rate_card" ? s.refEntered : ""}`}
          title={`${e.kind}: ${e.label}${e.enteredBy ? ` · ${e.enteredBy}` : ""}`}
        >
          {e.href ? <Link to={e.href}>{e.ref}</Link> : e.ref}
        </span>
      ))}
    </span>
  );
}

function ItemRow({
  item,
  out,
  open,
  onToggle,
  entered,
  onEnter,
  onDecide,
}: {
  item: DecisionItem;
  out: EngineOutput;
  open: boolean;
  onToggle: () => void;
  entered?: EnteredInputs[string];
  onEnter: (id: string, patch: EnteredInputs[string]) => void;
  onDecide: (item: DecisionItem, option: Option["key"], rationale: string, mitigation?: string) => Promise<void>;
}) {
  const place = item.mandatory.is ? "mandatory" : out.portfolio.chosen.includes(item) ? "chosen" : "slipped";
  return (
    <article className={`${s.item} ${s[place]}`} data-testid={`item-${item.findingId}`}>
      <button type="button" className={s.itemHead} onClick={onToggle} aria-expanded={open}>
        <span className={s.asset}>{item.asset}</span>
        <span className={s.headline}>{item.headline}</span>
        <span className={s.cat}>{item.category}</span>
        {item.mandatory.is ? (
          <span className={s.mand}>{t("not a decision")}</span>
        ) : (
          <span className={s.total}>
            {item.score.total != null ? item.score.total.toFixed(2) : "—"} <small>{item.score.coverage}</small>
          </span>
        )}
        <span className={s.place}>{t(place)}</span>
      </button>
      {!item.mandatory.is && (
        <div className={s.bars}>
          {item.score.components.map((c) => (
            <div key={c.key} className={s.bar} title={c.working}>
              <span className={s.barLabel}>{c.label}</span>
              <span className={s.barTrack}>
                {c.value != null && <span className={s.barFill} style={{ width: `${Math.round(c.value * 100)}%` }} />}
              </span>
              <span className={s.barVal}>{c.value != null ? c.value.toFixed(2) : "—"}</span>
            </div>
          ))}
        </div>
      )}
      {open && (
        <div className={s.detail}>
          {item.mandatory.is && (
            <p className={s.mandWhy}>
              {t(
                "Mandatory: {why}. It is done first; the solver subtracts its window, crew and cost before allocating anything else.",
                { why: String(item.mandatory.why) },
              )}
            </p>
          )}
          <dl className={s.facts}>
            <dt>{t("Condition")}</dt>
            <dd>
              {item.condition.label} <Refs list={item.condition.sources} />
            </dd>
            <dt>{t("Trend")}</dt>
            <dd>
              {item.trend.basis} <Refs list={item.trend.sources} />
            </dd>
            <dt>{t("Time to threshold")}</dt>
            <dd>
              {item.timeToThresholdDays != null ? t("{n} days", { n: item.timeToThresholdDays }) : t("not computable")}
            </dd>
            <dt>{t("Clause")}</dt>
            <dd>
              {item.clause.clause || t("none on the rule")} · {item.clause.effect.replace("_", " ")}{" "}
              <Refs list={item.clause.sources} />
            </dd>
            <dt>{t("Surface")}</dt>
            <dd>
              {item.surface.zoneClass}
              {item.surface.standType !== "n/a" ? ` (${item.surface.standType})` : ""} ·{" "}
              {item.surface.closureMinutes != null
                ? t("{n} min closure", { n: item.surface.closureMinutes })
                : t("closure minutes unknown")}{" "}
              ·{" "}
              {item.surface.movementsPerHour != null
                ? t("{n} movements/h", { n: item.surface.movementsPerHour })
                : t("movements unknown")}{" "}
              <Refs list={item.surface.sources} />
            </dd>
            <dt>{t("Cost curve")}</dt>
            <dd>
              {item.cost.now != null
                ? t("{now} now · {quarter} next quarter · {year} in a year · {twoYears} in two", {
                    now: money(item.cost.now, item.cost.currency),
                    quarter: money(item.cost.quarter ?? 0, item.cost.currency),
                    year: money(item.cost.year ?? 0, item.cost.currency),
                    twoYears: money(item.cost.twoYears ?? 0, item.cost.currency),
                  })
                : t("not priced")}{" "}
              <Refs list={item.cost.sources} />
            </dd>
            <dt>{t("Window")}</dt>
            <dd>
              {item.window.needsNight ? t("night window") : t("no night window")} · {item.window.crew ?? t("no crew")} ·{" "}
              {tn(item.window.crewNights, "{n} crew-night", "{n} crew-nights")}
              {item.window.season ? ` · ${item.window.season}` : ""} <Refs list={item.window.sources} />
            </dd>
          </dl>
          {!item.mandatory.is && (
            <div className={s.working}>
              {item.score.components.map((c) => (
                <div key={c.key}>
                  <b>{c.label}</b> {c.value != null ? c.value.toFixed(2) : "—"} — {c.working} <Refs list={c.sources} />
                </div>
              ))}
            </div>
          )}
          <div className={s.options}>
            {item.options.map((o) => (
              <div key={o.key} className={s.option}>
                <div className={s.optLabel}>{o.label}</div>
                <div className={s.optRow}>
                  <span>{t("cost")}</span>
                  <b>{o.cost != null ? money(o.cost, item.cost.currency) : "—"}</b>
                </div>
                <div className={s.optRow}>
                  <span>{t("capacity")}</span>
                  <b>{o.closureMinutes != null ? `${o.closureMinutes} min` : "—"}</b>
                </div>
                <div className={s.optRow}>
                  <span>{t("exposure after")}</span>
                  <b>{o.exposure != null ? o.exposure.toFixed(2) : "—"}</b>
                </div>
                {o.displaces.length > 0 && (
                  <div className={s.optRow}>
                    <span>{t("displaces")}</span>
                    <b>{o.displaces.join(", ")}</b>
                  </div>
                )}
                <p className={s.optNote}>{o.note}</p>
              </div>
            ))}
          </div>
          {item.missing.length > 0 && <MissingForm item={item} entered={entered} onEnter={onEnter} />}
          <DecideForm item={item} onDecide={onDecide} />
        </div>
      )}
    </article>
  );
}

function MissingForm({
  item,
  entered,
  onEnter,
}: {
  item: DecisionItem;
  entered?: EnteredInputs[string];
  onEnter: (id: string, patch: EnteredInputs[string]) => void;
}) {
  const [draft, setDraft] = useState<EnteredInputs[string]>(entered ?? {});
  const fields = new Set(item.missing.map((m) => m.field));
  const n = (k: keyof EnteredInputs[string]) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft({ ...draft, [k]: e.target.value === "" ? undefined : Number(e.target.value) });
  return (
    <form
      className={s.missing}
      onSubmit={(e) => {
        e.preventDefault();
        onEnter(item.findingId, draft);
      }}
    >
      <div className={s.missingHead}>
        {t("Missing on the platform — enter it, under your name, and it enters the engine as an entered source")}
      </div>
      <ul className={s.missingList}>
        {item.missing.map((m) => (
          <li key={m.field}>{m.why}</li>
        ))}
      </ul>
      <div className={s.grid4}>
        {fields.has("condition") && (
          <>
            <Field label="Measured value">
              <Input type="number" step="any" value={draft.conditionValue ?? ""} onChange={n("conditionValue")} />
            </Field>
            <Field label="Limit">
              <Input type="number" step="any" value={draft.conditionLimit ?? ""} onChange={n("conditionLimit")} />
            </Field>
          </>
        )}
        {fields.has("trend") && (
          <>
            <Field label="Rate per day (signed)">
              <Input type="number" step="any" value={draft.ratePerDay ?? ""} onChange={n("ratePerDay")} />
            </Field>
            <Field label="Where the rate comes from">
              <Input
                value={draft.trendBasis ?? ""}
                onChange={(e) => setDraft({ ...draft, trendBasis: e.target.value })}
                placeholder={t("e.g. two surveys, Mar and Aug")}
              />
            </Field>
          </>
        )}
        {fields.has("closureMinutes") && (
          <Field label="Closure the work needs (min)">
            <Input type="number" value={draft.closureMinutes ?? ""} onChange={n("closureMinutes")} />
          </Field>
        )}
        {fields.has("movementsPerHour") && (
          <Field label="Movements per hour">
            <Input type="number" step="any" value={draft.movementsPerHour ?? ""} onChange={n("movementsPerHour")} />
          </Field>
        )}
        {fields.has("costNow") && (
          <Field label="Repair cost now">
            <Input type="number" value={draft.costNow ?? ""} onChange={n("costNow")} />
          </Field>
        )}
        {fields.has("window") && (
          <>
            <Field label="Crew">
              <Input value={draft.crew ?? ""} onChange={(e) => setDraft({ ...draft, crew: e.target.value })} />
            </Field>
            <Field label="Crew-nights">
              <Input type="number" value={draft.crewNights ?? ""} onChange={n("crewNights")} />
            </Field>
            <Field label="Needs a night window">
              <Select
                value={draft.needsNight == null ? "" : draft.needsNight ? "yes" : "no"}
                onChange={(e) =>
                  setDraft({ ...draft, needsNight: e.target.value === "" ? undefined : e.target.value === "yes" })
                }
              >
                <option value="">{t("as the engine assumed")}</option>
                <option value="yes">{t("yes")}</option>
                <option value="no">{t("no")}</option>
              </Select>
            </Field>
          </>
        )}
      </div>
      <div className={s.row}>
        <Button type="submit" size="sm">
          {t("Enter these")}
        </Button>
      </div>
    </form>
  );
}

function DecideForm({
  item,
  onDecide,
}: {
  item: DecisionItem;
  onDecide: (item: DecisionItem, option: Option["key"], rationale: string, mitigation?: string) => Promise<void>;
}) {
  const [option, setOption] = useState<Option["key"]>(item.mandatory.is ? "act_now" : "defer_mitigate");
  const [rationale, setRationale] = useState("");
  const [mitigation, setMitigation] = useState(item.options[1].mitigation ?? "");
  const [done, setDone] = useState(false);
  if (done)
    return (
      <p className={s.logged}>{t("Logged. It is on the decision log below, with the evidence that was on screen.")}</p>
    );
  return (
    <form
      className={s.decide}
      onSubmit={(e) => {
        e.preventDefault();
        if (!rationale.trim()) return;
        void onDecide(item, option, rationale.trim(), option === "defer_mitigate" ? mitigation : undefined).then(() =>
          setDone(true),
        );
      }}
    >
      <div className={s.missingHead}>
        {t("Log the decision — what was chosen, by whom, on what evidence, with what mitigation")}
      </div>
      <div className={s.grid4}>
        <Field label="Option">
          <Select
            value={option}
            onChange={(e) => setOption(e.target.value as Option["key"])}
            aria-label={t("decision for {name}", { name: item.asset })}
          >
            {item.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        {option === "defer_mitigate" && (
          <Field label="Mitigation">
            <Input value={mitigation} onChange={(e) => setMitigation(e.target.value)} />
          </Field>
        )}
        <Field label="Rationale">
          <Input
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder={t("why this, in a sentence a regulator will read")}
            aria-label={t("rationale for {name}", { name: item.asset })}
          />
        </Field>
      </div>
      <div className={s.row}>
        <Button type="submit" size="sm" variant="primary" disabled={!rationale.trim()}>
          {t("Log this decision")}
        </Button>
      </div>
    </form>
  );
}

/* ── portfolio ────────────────────────────────────────────────────────── */

function PortfolioView({ out }: { out: EngineOutput }) {
  const p = out.portfolio;
  const cur = out.items[0]?.cost.currency ?? "SAR";
  return (
    <Card
      title="The portfolio"
      sub={t("{quarter}: what fits, what slips, and what each slip costs", { quarter: p.constraints.quarter })}
      className={s.portfolio}
    >
      {p.infeasible && <p className={s.infeasible}>{p.infeasible}</p>}
      <div className={s.grid4}>
        <div className={s.res}>
          <span>{t("windows")}</span>
          <b>
            {p.used.windows} / {p.available.windows}
          </b>
        </div>
        <div className={s.res}>
          <span>{t("budget")}</span>
          <b>
            {money(p.used.budget, cur)} / {money(p.available.budget, cur)}
          </b>
        </div>
        <div className={s.res}>
          <span>{t("crew-nights")}</span>
          <b>
            {p.used.crewNights} / {p.available.crewNights}
          </b>
        </div>
        <div className={s.res}>
          <span>{t("objective")}</span>
          <b>{p.objective}</b>
        </div>
      </div>
      <div className={s.cols3}>
        <div>
          <div className={s.colTitle}>{t("Mandatory first")}</div>
          {p.mandatory.map((i) => (
            <div key={i.id} className={s.line}>
              {i.asset} — {i.headline}
            </div>
          ))}
          {!p.mandatory.length && <div className={s.note}>{t("none")}</div>}
        </div>
        <div>
          <div className={s.colTitle}>{t("Chosen")}</div>
          {p.chosen.map((i) => (
            <div key={i.id} className={s.line}>
              {i.asset} — {i.headline}
              {i.options[0].displaces.length ? (
                <span className={s.displaces}>
                  {" "}
                  {t("displaces")} {i.options[0].displaces.join(", ")}
                </span>
              ) : null}
            </div>
          ))}
          {!p.chosen.length && <div className={s.note}>{t("none")}</div>}
        </div>
        <div>
          <div className={s.colTitle}>{t("Slipped, and what it costs")}</div>
          {p.slipped.map((x) => (
            <div key={x.item.id} className={s.line}>
              {x.item.asset} — {x.why}; <b>{x.slipCost != null ? money(x.slipCost, cur) : t("not priced")}</b>
            </div>
          ))}
          {!p.slipped.length && <div className={s.note}>{t("none")}</div>}
        </div>
      </div>
    </Card>
  );
}

/* ── the case, the model, follow-ups ──────────────────────────────────── */

function CaseView({ out, onResolve }: { out: EngineOutput; onResolve: (patch: Partial<Constraints>) => void }) {
  const cfg = useMemo(() => loadConfig(), []);
  const connected = hasModel(cfg);
  const [modelText, setModelText] = useState<string | null>(null);
  const [modelNote, setModelNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [answers, setAnswers] = useState<{ q: string; a: string; from: string }[]>([]);

  const explain = async () => {
    setBusy(true);
    try {
      const reply = await askModel(
        cfg,
        MODEL_SYSTEM_PROMPT,
        `Explain this allocation to the chief executive, then give the strongest argument against it.\n\n${materialForModel(out)}`,
      );
      const bad = verifyFigures(reply.text, out.figures);
      if (bad.length) {
        setModelText(null);
        setModelNote(
          bad.length === 1
            ? t(
                "The model's explanation was withheld: it stated a figure the engine did not produce ({figures}). The engine's own case stands.",
                { figures: bad.slice(0, 4).join(", ") },
              )
            : t(
                "The model's explanation was withheld: it stated figures the engine did not produce ({figures}). The engine's own case stands.",
                { figures: bad.slice(0, 4).join(", ") },
              ),
        );
      } else {
        setModelText(reply.text);
        setModelNote(
          t("Explained by {model}; every figure checked against the engine's output.", { model: reply.model }),
        );
      }
    } catch (e) {
      setModelNote(t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  const ask = async () => {
    const text = q.trim();
    if (!text) return;
    const own = answerFollowUp(text, out);
    if (own) {
      setAnswers((a) => [{ q: text, a: own.text, from: t("the engine") }, ...a]);
      if (own.resolveWith) onResolve(own.resolveWith);
      setQ("");
      return;
    }
    if (!connected) {
      setAnswers((a) => [
        {
          q: text,
          a: t(
            'The engine answers questions about an item, a component, the slipped set, the mandatory set, or a changed constraint ("what if we had 10 windows"). Connect a model in Settings for anything wider.',
          ),
          from: t("the engine"),
        },
        ...a,
      ]);
      setQ("");
      return;
    }
    setBusy(true);
    try {
      const reply = await askModel(
        cfg,
        MODEL_SYSTEM_PROMPT,
        `Question: ${text}\n\nMaterial:\n${materialForModel(out)}`,
      );
      const bad = verifyFigures(reply.text, out.figures);
      setAnswers((a) => [
        {
          q: text,
          a: bad.length
            ? t("Withheld: the reply stated figures the engine did not produce ({figures}).", {
                figures: bad.slice(0, 4).join(", "),
              })
            : reply.text,
          from: bad.length ? t("withheld") : reply.model,
        },
        ...a,
      ]);
    } catch (e) {
      setAnswers((a) => [
        {
          q: text,
          a: t("The model could not be reached: {error}", { error: e instanceof Error ? e.message : String(e) }),
          from: t("error"),
        },
        ...a,
      ]);
    } finally {
      setBusy(false);
      setQ("");
    }
  };

  return (
    <Card
      title="The executive case"
      sub="written from the solver output; a connected model may explain it, and is checked against it"
    >
      <div className={s.case}>
        <p className={s.rec}>{out.case.recommendation}</p>
        <div className={s.colTitle}>{t("Reasoning chain")}</div>
        <ol className={s.chain}>
          {out.case.reasoning.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ol>
        <div className={s.colTitle}>{t("The strongest counter-argument")}</div>
        <p>{out.case.counterArgument}</p>
        <div className={s.colTitle}>{t("What would change the answer")}</div>
        <ul className={s.chain}>
          {out.case.wouldChange.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
        <div className={s.row}>
          <Button onClick={() => void explain()} disabled={!connected || busy}>
            {connected
              ? busy
                ? t("Asking…")
                : t("Ask the model to explain and challenge")
              : t("No model connected — Settings › Sky › beyond the platform")}
          </Button>
          {modelNote && <span className={s.note}>{modelNote}</span>}
        </div>
        {modelText && <blockquote className={s.model}>{modelText}</blockquote>}
      </div>
      <form
        className={s.ask}
        onSubmit={(e) => {
          e.preventDefault();
          void ask();
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Why is RWY-17-35 above the bridge? What if we had 10 windows? What slips?")}
          aria-label={t("Follow-up question")}
        />
        <Button type="submit" variant="primary" disabled={busy || !q.trim()}>
          {t("Ask")}
        </Button>
      </form>
      {answers.length > 0 && (
        <div className={s.answers}>
          {answers.map((a, i) => (
            <div key={i} className={s.answer}>
              <div className={s.q}>{a.q}</div>
              <pre className={s.a}>{a.a}</pre>
              <div className={s.from}>{a.from}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ── the rate card ────────────────────────────────────────────────────── */

export function RateCardEditor({
  card,
  onChange,
  onSave,
}: {
  card: RateCard;
  onChange: (c: RateCard) => void;
  onSave: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card
      title="Rate card"
      sub={
        card.enteredBy
          ? t("edited by {name} on {date}", { name: card.enteredBy, date: String(card.enteredAt?.slice(0, 10)) })
          : "the starting card — coarse placeholders until the airport edits them; actual work-order costs override it per item"
      }
    >
      <div className={s.row}>
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? t("Hide the card") : t("Edit the card")}
        </Button>
        {open && (
          <Button size="sm" onClick={onSave}>
            {t("Save the card")}
          </Button>
        )}
      </div>
      {open && (
        <div className={s.card}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("Category")}</th>
                <th>{t("Unit repair now ({currency})", { currency: card.currency })}</th>
                <th>{t("Escalation per quarter")}</th>
                <th>{t("Mitigation cost")}</th>
                <th>{t("Mitigation")}</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((k) => {
                const row = card.categories[k];
                const set = (patch: Partial<typeof row>) =>
                  onChange({ ...card, categories: { ...card.categories, [k]: { ...row, ...patch } } });
                return (
                  <tr key={k}>
                    <td>{k}</td>
                    <td>
                      <Input
                        type="number"
                        value={row.unitRepairNow}
                        onChange={(e) => set({ unitRepairNow: Number(e.target.value) || 0 })}
                        aria-label={t("{category} unit repair", { category: k })}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        step="0.01"
                        value={row.escalationPerQuarter}
                        onChange={(e) => set({ escalationPerQuarter: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        value={row.mitigationCost}
                        onChange={(e) => set({ mitigationCost: Number(e.target.value) || 0 })}
                      />
                    </td>
                    <td>
                      <Input value={row.mitigation} onChange={(e) => set({ mitigation: e.target.value })} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("Zone class")}</th>
                <th>{t("Cost per closure hour ({currency})", { currency: card.currency })}</th>
                <th>{t("Movements per hour (when the capacity model has no coverage)")}</th>
              </tr>
            </thead>
            <tbody>
              {ZONE_CLASSES.map((z) => (
                <tr key={z}>
                  <td>{z}</td>
                  <td>
                    <Input
                      type="number"
                      value={card.closureCostPerHour[z]}
                      onChange={(e) =>
                        onChange({
                          ...card,
                          closureCostPerHour: { ...card.closureCostPerHour, [z]: Number(e.target.value) || 0 },
                        })
                      }
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      step="any"
                      value={card.movementsPerHour[z]}
                      onChange={(e) =>
                        onChange({
                          ...card,
                          movementsPerHour: { ...card.movementsPerHour, [z]: Number(e.target.value) || 0 },
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── the log ──────────────────────────────────────────────────────────── */

function LogView({ log }: { log: DecisionRecord[] }) {
  return (
    <Card
      title="Decision log"
      sub="what was chosen, by whom, on what evidence, with what mitigation — the record that calibrates the prediction ledger and stands after an incident"
    >
      {log.length === 0 ? (
        <p className={s.note}>{t("Nothing logged yet.")}</p>
      ) : (
        <div className={s.log}>
          {log.map((r) => (
            <div key={r.id} className={s.logRow} data-testid="log-row">
              <div className={s.logHead}>
                <b>{r.asset}</b> — {r.headline} · <span className={s.opt}>{r.option.replace("_", " ")}</span> · {r.by} ·{" "}
                {r.at.replace("T", " ").slice(0, 16)}
              </div>
              <div className={s.logBody}>
                {r.rationale}
                {r.mitigation ? t(" — mitigation: {mitigation}", { mitigation: r.mitigation }) : ""}
                {r.timeToThresholdDays != null
                  ? t(" — threshold in {n} days at the time", { n: r.timeToThresholdDays })
                  : ""}{" "}
                ·{" "}
                {t("score {score} on {coverage}", {
                  score: r.scoreAtDecision.total ?? "—",
                  coverage: r.scoreAtDecision.coverage,
                })}
              </div>
              <div className={s.logRefs}>
                <Refs list={r.evidence} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
